import axioInstance from "../../custom_utilities/AxiosInstance/axiosInstance";
import {
  url_282,
  url_308,
  url_309,
  url_310,
  url_311,
  url_312,
  url_313,
  url_316,
  url_317,
  url_318,
  url_319,
} from "../../custom_utilities/api_services";

const MISTAKE_BOOK_PAPER_TYPE = 22;

/** Drop undefined / null / "" / "undefined" / "null" so query strings stay clean. */
const buildCleanParams = (params = {}) => {
  const query = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const text = String(value).trim();
    if (!text || text.toLowerCase() === "undefined" || text.toLowerCase() === "null") {
      return;
    }
    query[key] = value;
  });
  return query;
};

const unwrap = (response) => {
  const body = response?.data;
  if (body?.status === 200 || body?.status === "200") {
    return body.data ?? body;
  }
  return body?.data ?? body ?? null;
};

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const ERROR_CATEGORY_COLORS = {
  conceptual: "#47adfb",
  "conceptual gap": "#47adfb",
  formula: "#ffbc4c",
  "formula error": "#ffbc4c",
  "time pressure": "#ff7563",
  untagged: "#c9c9c9",
  "silly mistake": "#3dd665",
  silly: "#3dd665",
  calculation: "#8047fb",
  "calculation error": "#8047fb",
  guessed: "#c247fb",
};

const ZONE_STYLES = {
  mastery: { tone: "success", label: "Mastery Zone", hint: "Fast & Correct" },
  caution: { tone: "warning", label: "Caution Zone", hint: "Fast & Incorrect" },
  struggle: { tone: "error", label: "Struggle Zone", hint: "Slow & Incorrect" },
  rush: { tone: "info", label: "Rush Zone", hint: "Slow & Correct" },
};

export const normalizeMistakeTopic = (topic = {}, index = 0) => {
  const mistakes = toNumber(
    topic.wrong_attempts ??
      topic.mistakes ??
      topic.mistake_count ??
      topic.total_mistakes ??
      topic.wrong ??
      topic.errors
  );
  const resolved = toNumber(
    topic.resolved_count ??
      topic.resolved ??
      topic.resolved_mistakes
  );
  const neu = toNumber(topic.new ?? topic.new_count ?? topic.new_mistakes);
  const percentRaw =
    topic.percent ??
    topic.percentage ??
    topic.resolved_percent ??
    topic.accuracy ??
    (mistakes > 0 ? Math.round((resolved / mistakes) * 100) : 0);

  return {
    id:
      topic.topic_id ??
      topic.id ??
      topic.topic_name ??
      topic.name ??
      `topic-${index}`,
    name: topic.topic_name ?? topic.name ?? topic.title ?? `Topic ${index + 1}`,
    mistakes,
    resolved,
    newCount: neu,
    percent: Math.max(0, Math.min(100, Math.round(toNumber(percentRaw)))),
    accuracy: toNumber(
      topic.accuracy ?? topic.accuracy_percent ?? topic.percent ?? percentRaw
    ),
    errors: toNumber(
      topic.errors ??
        topic.error_count ??
        topic.wrong_attempts ??
        topic.mistakes ??
        mistakes
    ),
    subjectId: topic.subject_id ?? topic.subjectId,
    subjectName: topic.subject_name ?? topic.subject ?? "",
    weightageAdv: toNumber(
      topic.adv_weightage ?? topic.weightage_adv ?? topic.advanced_weightage ?? 10
    ),
    weightageMains: toNumber(
      topic.mains_weightage ?? topic.weightage_mains ?? topic.main_weightage ?? 7
    ),
    icon: topic.topic_icon ?? topic.icon ?? topic.image ?? "",
    raw: topic,
  };
};

export const normalizeMistakeSubject = (subject = {}, index = 0) => ({
  id: subject.subject_id ?? subject.id ?? `subject-${index}`,
  name: subject.subject_name ?? subject.name ?? `Subject ${index + 1}`,
  icon: subject.icon ?? subject.image ?? "",
  raw: subject,
});

const normalizeErrorBreakdown = (data = {}) => {
  const raw =
    data.breakdown ||
    data.error_breakdown ||
    data.errorBreakdown ||
    data.errors_breakdown ||
    data.mistake_reasons ||
    data.reasons ||
    [];

  if (!Array.isArray(raw) || !raw.length) {
    return [];
  }

  return raw.map((item, index) => {
    const label =
      item.mistake_tag ||
      item.label ||
      item.name ||
      item.reason ||
      item.type ||
      `Category ${index + 1}`;
    const key = String(label).toLowerCase();
    return {
      id: item.id ?? key ?? `err-${index}`,
      label,
      percent: Math.max(
        0,
        Math.round(toNumber(item.percent ?? item.percentage ?? item.value))
      ),
      color:
        item.color ||
        ERROR_CATEGORY_COLORS[key] ||
        ["#47adfb", "#ffbc4c", "#ff7563", "#c9c9c9", "#3dd665", "#8047fb", "#c247fb"][
          index % 7
        ],
      raw: item,
    };
  });
};

const normalizeTimeZones = (data = {}) => {
  const report =
    data.time_quality_report ||
    data.time_quality ||
    data.timeQuality ||
    data.time_zones ||
    data.zones ||
    null;

  let raw = [];

  if (Array.isArray(report)) {
    raw = report;
  } else if (report && typeof report === "object") {
    const zoneMap = report.data && typeof report.data === "object" ? report.data : report;
    raw = Object.entries(zoneMap)
      .filter(([, value]) => value && typeof value === "object" && !Array.isArray(value))
      .filter(([key]) => !["title", "subtitle"].includes(key))
      .map(([key, value]) => ({
        id: key,
        zone: key.replace(/_zone$/, ""),
        label: value.title || value.label || value.name,
        percent: value.percentage ?? value.percent,
        percentage: value.percentage ?? value.percent,
        count: value.count,
        hint: value.description || value.hint || value.subtitle,
        description: value.description,
        raw: value,
      }));
  }

  if (!raw.length) {
    return [];
  }

  return raw.map((item, index) => {
    const key = String(
      item.zone || item.id || item.label || item.name || ""
    )
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/_zone$/, "");
    const preset = ZONE_STYLES[key] || {};
    return {
      id: item.id ?? key ?? `zone-${index}`,
      percent: Math.max(
        0,
        Math.round(toNumber(item.percent ?? item.percentage ?? item.value))
      ),
      count: toNumber(item.count),
      label: item.label || item.name || preset.label || `Zone ${index + 1}`,
      hint: item.hint || item.subtitle || item.description || preset.hint || "",
      tone: item.tone || preset.tone || "warning",
      raw: item,
    };
  });
};

export const normalizeMistakeAnalytics = (data = {}) => {
  const whyWrong =
    data.why_getting_wrong && typeof data.why_getting_wrong === "object"
      ? data.why_getting_wrong
      : {};
  const timeReport =
    data.time_quality_report && typeof data.time_quality_report === "object"
      ? data.time_quality_report
      : {};

  const topicsSource =
    data.top_weak_topics ||
    data.weak_topics ||
    data.topics ||
    data.top_topics ||
    [];
  const topics = (Array.isArray(topicsSource) ? topicsSource : [])
    .map(normalizeMistakeTopic)
    .slice(0, 3);

  const names = topics.map((t) => t.name).filter(Boolean);
  const totalErrors = toNumber(
    data.total_errors ?? data.total_mistakes ?? data.error_count
  );
  const resolvedCount = toNumber(data.resolved_count ?? data.resolved);
  const unresolvedCount = toNumber(
    data.unresolved_count ?? data.unresolved,
    Math.max(totalErrors - resolvedCount, 0)
  );

  const currentAccuracy = toNumber(
    data.current_accuracy ??
      data.accuracy ??
      data.accuracy_percent ??
      data.user_accuracy,
    -1
  );
  const lift = toNumber(
    data.accuracy_lift ??
      data.accuracy_improvement ??
      data.lift_percent ??
      data.suggested_accuracy_lift,
    15
  );
  const resolvedAccuracy =
    currentAccuracy >= 0
      ? currentAccuracy
      : totalErrors > 0
        ? Math.round((resolvedCount / totalErrors) * 100)
        : 0;
  const projectedAccuracy = toNumber(
    data.projected_accuracy ??
      data.potential_accuracy ??
      resolvedAccuracy + lift,
    resolvedAccuracy + lift
  );

  const errorBreakdown = normalizeErrorBreakdown(data);
  const topCategory = [...errorBreakdown].sort(
    (a, b) => b.percent - a.percent
  )[0];

  return {
    title: data.title || "Weak Area Analysis",
    subtitle:
      data.subtitle ||
      data.message ||
      (names.length
        ? `Top 3 weak topics: ${names.join(", ")} — fix these to lift accuracy by ~${lift}%.`
        : "Review your weakest topics and practice to improve accuracy."),
    topics: names,
    topWeakTopics: topics,
    lift,
    currentAccuracy: resolvedAccuracy,
    projectedAccuracy,
    resolvedCount,
    unresolvedCount,
    insightPrimary: `Your accuracy is ${resolvedAccuracy}%. Closing your top 3 weak topics could lift it to ~${projectedAccuracy}%.`,
    insightSecondary:
      data.insight_subtitle ||
      data.based_on ||
      "Based on your attempted questions across last few days.",
    whyWrongTitle: whyWrong.title || "Why you're getting it wrong",
    whyWrongSubtitle:
      whyWrong.subtitle || "Error breakdown across all mistakes",
    errorBreakdown,
    totalErrors,
    errorTip:
      whyWrong.insight ||
      data.error_tip ||
      data.insight_tip ||
      (topCategory
        ? `${topCategory.percent}% of your mistakes are ${topCategory.label} - focus on theory revision first.`
        : "Focus on your weakest topics first to lift accuracy."),
    timeQualityTitle: timeReport.title || "Time Quality Report",
    timeQualitySubtitle:
      timeReport.subtitle || "How your speed pairs with accuracy",
    timeZones: normalizeTimeZones(data),
    attemptFilters: Array.isArray(data.attempt_filters)
      ? data.attempt_filters
      : ["Attempt Last", "Last 7 days", "Last 30 days", "All time"],
    raw: data,
  };
};

export const fetchUserWrongTopics = async (params = {}) => {
  const subjectName =
    params.subject || params.subject_name || params.subjectName || "";
  const query = subjectName
    ? { subject: subjectName }
    : { ...params };

  const response = await axioInstance.get(url_308, { params: query });
  const body = response?.data;
  const data = body?.data ?? null;
  const weakAnalysisRaw = body?.weak_analysis ?? null;

  const weakAnalysis = weakAnalysisRaw
    ? {
        title: weakAnalysisRaw.title || "Weak Area Analysis",
        subtitle: weakAnalysisRaw.description || "",
        image: weakAnalysisRaw.image || "",
        raw: weakAnalysisRaw,
      }
    : null;

  // API shape: data = [{ subject, topics: [...] }]
  if (Array.isArray(data)) {
    const hasGroupedTopics = data.some((entry) => Array.isArray(entry?.topics));

    if (hasGroupedTopics) {
      const subjects = data.map((entry, index) =>
        normalizeMistakeSubject({
          id: String(entry.subject || "")
            .trim()
            .toLowerCase() || `subject-${index}`,
          name: entry.subject || `Subject ${index + 1}`,
          raw: entry,
        })
      );

      const topics = data.flatMap((entry) => {
        const subjectNameFromEntry = entry?.subject || subjectName || "";
        return (Array.isArray(entry.topics) ? entry.topics : []).map(
          (topic, index) =>
            normalizeMistakeTopic(
              {
                ...topic,
                subject: subjectNameFromEntry,
                subject_name: subjectNameFromEntry,
              },
              index
            )
        );
      });

      return {
        subjects,
        topics,
        years: [],
        weakAnalysis,
        raw: body,
      };
    }

    return {
      subjects: [],
      topics: data.map(normalizeMistakeTopic),
      years: [],
      weakAnalysis,
      raw: body,
    };
  }

  if (!data) {
    return { subjects: [], topics: [], years: [], weakAnalysis, raw: body };
  }

  const subjectsSource =
    data.subjects || data.subject_list || data.subjectList || [];
  const topicsSource =
    data.topics ||
    data.topic_list ||
    data.wrong_topics ||
    data.user_wrong_topics ||
    data.list ||
    [];
  const yearsSource = data.years || data.year_list || data.yearList || [];

  return {
    subjects: (Array.isArray(subjectsSource) ? subjectsSource : []).map(
      normalizeMistakeSubject
    ),
    topics: (Array.isArray(topicsSource) ? topicsSource : []).map(
      normalizeMistakeTopic
    ),
    years: (Array.isArray(yearsSource) ? yearsSource : [])
      .map((y) => Number(y?.year ?? y))
      .filter((y) => Number.isFinite(y)),
    weakAnalysis,
    raw: body,
  };
};

export const fetchMistakeAnalytics = async (params = {}) => {
  const subjectName =
    params.subject || params.subject_name || params.subjectName || "";
  const query = subjectName
    ? {
        subject: subjectName,
        ...(params.year ? { year: params.year } : {}),
        ...(params.attempt_filter
          ? { attempt_filter: params.attempt_filter }
          : {}),
      }
    : { ...params };

  const response = await axioInstance.get(url_309, { params: query });
  const body = response?.data;
  const data = body?.data ?? {};

  if (!(body?.status === 200 || body?.status === "200")) {
    const error = new Error(
      body?.message || "Unable to load Weak Area Analysis right now."
    );
    error.response = { data: body };
    throw error;
  }

  return normalizeMistakeAnalytics(data || {});
};

export const normalizePracticeSet = (set = {}, index = 0) => {
  const setNumber = toNumber(set.set_number ?? set.setNumber ?? index + 1);
  const questionCount = toNumber(
    set.question_count ??
      set.questionCount ??
      set.total_questions ??
      set.total_q ??
      set.questions_count ??
      (Array.isArray(set.qids) ? set.qids.length : 0)
  );
  const status = String(
    set.status ||
      set.set_status ||
      set.progress_status ||
      set.completion_status ||
      set.attempt_status ||
      ""
  ).toLowerCase();
  const actionLabel = String(
    set.action_label || set.button_label || set.action || ""
  ).toLowerCase();

  const flagOn = (value) => {
    if (value === true || value === 1 || value === "1") return true;
    if (value === false || value === 0 || value === "0" || value == null || value === "") {
      return false;
    }
    if (typeof value === "string") {
      const lower = value.trim().toLowerCase();
      return (
        lower === "true" ||
        lower === "yes" ||
        lower === "completed" ||
        lower === "complete" ||
        lower === "submitted"
      );
    }
    return false;
  };

  const isCompleted =
    status.includes("complete") ||
    status.includes("submitted") ||
    status.includes("finished") ||
    status.includes("done") ||
    actionLabel.includes("reattempt") ||
    actionLabel.includes("re-attempt") ||
    actionLabel.includes("view result") ||
    actionLabel.includes("view analytics") ||
    (actionLabel.includes("analytics") && !actionLabel.includes("start")) ||
    flagOn(set.is_completed) ||
    flagOn(set.completed) ||
    flagOn(set.is_submit) ||
    flagOn(set.is_submitted) ||
    flagOn(set.submitted) ||
    flagOn(set.submit_status) ||
    flagOn(set.completed_status);
  const isResume =
    !isCompleted &&
    (actionLabel.includes("resume") ||
      status.includes("resume") ||
      status.includes("progress") ||
      status.includes("ongoing") ||
      flagOn(set.is_resume) ||
      flagOn(set.in_progress));

  return {
    id: set.set_id ?? set.id ?? `set-${setNumber}`,
    setId: set.set_id ?? set.id ?? null,
    setNumber,
    title: set.title || set.name || set.set_name || `Set ${setNumber}`,
    questionCount,
    subject: set.subject || set.subject_name || "",
    dateLabel:
      set.date_label ||
      set.created_at_label ||
      set.created_at ||
      set.created_on ||
      set.created_date ||
      set.date ||
      "",
    isCompleted,
    isResume,
    qids: Array.isArray(set.qids) ? set.qids : [],
    remainingAfterThisSet: toNumber(
      set.remaining_after_this_set ?? set.remainingAfterThisSet
    ),
    raw: set,
  };
};

export const normalizeCreatedPracticeSet = (data = {}) =>
  normalizePracticeSet(data);

const extractPracticeSetLists = (data) => {
  if (Array.isArray(data)) {
    return { active: data, completed: [] };
  }

  if (!data || typeof data !== "object") {
    return { active: [], completed: [] };
  }

  const active =
    (Array.isArray(data.practice_sets) && data.practice_sets) ||
    (Array.isArray(data.sets) && data.sets) ||
    (Array.isArray(data.active_sets) && data.active_sets) ||
    (Array.isArray(data.list) && data.list) ||
    (Array.isArray(data.data) && data.data) ||
    [];

  const completed =
    (Array.isArray(data.completed_sets) && data.completed_sets) ||
    (Array.isArray(data.completed) && data.completed) ||
    (Array.isArray(data.completedPracticeSets) && data.completedPracticeSets) ||
    (Array.isArray(data.finished_sets) && data.finished_sets) ||
    [];

  return { active, completed };
};

/**
 * getPracticeSets — GET /api/v1/getPracticeSets?subject=Physics
 */
export const fetchPracticeSets = async (params = {}) => {
  const subject =
    params.subject || params.subject_name || params.subjectName || "";
  const query = subject ? { subject } : { ...params };

  const response = await axioInstance.get(url_310, { params: query });
  const body = response?.data;
  const data = body?.data ?? null;
  const { active, completed } = extractPracticeSetLists(data);

  const activeSets = active.map(normalizePracticeSet);
  const completedSets = completed.map((set, index) => ({
    ...normalizePracticeSet(set, index),
    isCompleted: true,
    isResume: false,
  }));

  // Prefer explicit completed list; otherwise rely on per-set flags in active.
  let sets;
  if (completedSets.length > 0) {
    const completedIds = new Set(
      completedSets.map((set) => String(set.setId ?? set.id))
    );
    sets = [
      ...activeSets.filter(
        (set) => !completedIds.has(String(set.setId ?? set.id))
      ),
      ...completedSets,
    ];
  } else {
    sets = activeSets;
  }

  if (
    body &&
    !(body.status === 200 || body.status === "200") &&
    !sets.length
  ) {
    const error = new Error(
      body?.message || "Unable to load practice sets right now."
    );
    error.response = { data: body };
    throw error;
  }

  return {
    sets,
    remainingQuestions: toNumber(
      body?.remaining_questions ??
        body?.remaining ??
        data?.remaining_questions ??
        data?.remaining ??
        data?.available_questions ??
        data?.questions_remaining,
      -1
    ),
    raw: body,
  };
};

/**
 * createPracticeSet — { subject: "Physics", question_count: 10|15|25 }
 */
export const createPracticeSet = async (payload = {}) => {
  const subject = String(payload.subject || "").trim();
  const questionCount = Number(payload.question_count ?? payload.questionCount ?? 10);

  const bodyPayload = {
    subject,
    question_count: [10, 15, 25].includes(questionCount) ? questionCount : 10,
  };

  const response = await axioInstance.post(url_311, bodyPayload);
  const body = response?.data;

  if (!(body?.status === 200 || body?.status === "200")) {
    const error = new Error(
      body?.message || "Could not create practice set. Please try again."
    );
    error.response = { data: body };
    throw error;
  }

  return {
    message: body?.message || "Practice set created successfully.",
    data: normalizeCreatedPracticeSet(body?.data || {}),
  };
};

/**
 * Mistake Book practice-set instructions
 * GET /api/v1/testdetails?paper_type=22&set_id=13&subject=Physics
 * (never send paper_id/test_id/planner_id as undefined|null)
 */
export const fetchPracticeSetTestDetails = async (params = {}) => {
  const setId = params.set_id ?? params.setId;
  const subject = String(params.subject || params.subject_name || "").trim();
  const paperType =
    Number(params.paper_type ?? params.paperType ?? MISTAKE_BOOK_PAPER_TYPE) ||
    MISTAKE_BOOK_PAPER_TYPE;
  const topicName = String(params.topic_name ?? params.topicName ?? "").trim();

  const query = buildCleanParams({
    paper_type: paperType,
    set_id: setId,
    subject: subject || undefined,
    topic_name: topicName || undefined,
  });

  const response = await axioInstance.get(url_316, { params: query });
  const body = response?.data;

  if (!(body?.status === 200 || body?.status === "200")) {
    const error = new Error(
      body?.message || "Unable to load practice set instructions."
    );
    error.response = { data: body };
    throw error;
  }

  return {
    instruction: body?.data ?? {},
    raw: body,
    query,
  };
};

/**
 * Optional practice-mode instruction variant
 * GET /api/v1/pyq_subjecttopic_instruction?paper_type=22&set_id=...
 */
export const fetchPracticeSetPyqInstruction = async (params = {}) => {
  const setId = params.set_id ?? params.setId;
  const subject = String(params.subject || params.subject_name || "").trim();
  const paperType =
    Number(params.paper_type ?? params.paperType ?? MISTAKE_BOOK_PAPER_TYPE) ||
    MISTAKE_BOOK_PAPER_TYPE;

  const query = buildCleanParams({
    paper_type: paperType,
    set_id: setId,
    subject: subject || undefined,
  });

  const response = await axioInstance.get(url_319, { params: query });
  const body = response?.data;

  if (!(body?.status === 200 || body?.status === "200")) {
    const error = new Error(
      body?.message || "Unable to load practice set instructions."
    );
    error.response = { data: body };
    throw error;
  }

  return {
    instruction: body?.data ?? {},
    raw: body,
    query,
  };
};

/**
 * Mistake Book practice-set questions for the exact set
 * practice → /api/v1/allquestionlist_practice
 * test     → /api/v1/allquestionlist
 */
export const fetchPracticeSetQuestionList = async (params = {}) => {
  const setId = params.set_id ?? params.setId;
  const subject = String(params.subject || params.subject_name || "").trim();
  const paperType =
    Number(params.paper_type ?? params.paperType ?? MISTAKE_BOOK_PAPER_TYPE) ||
    MISTAKE_BOOK_PAPER_TYPE;
  const practiceMode = String(params.practiceMode || params.practice_mode || "practice")
    .trim()
    .toLowerCase();
  const gamificationOn = Boolean(params.gamificationOn ?? params.gamification_on);

  const url = practiceMode === "practice" ? url_317 : url_318;
  const query = buildCleanParams({
    paper_type: paperType,
    set_id: setId,
    subject: subject || undefined,
    gamification_status:
      practiceMode === "practice" && gamificationOn ? 1 : undefined,
  });

  const response = await axioInstance.get(url, { params: query });
  const body = response?.data;

  if (!(body?.status === 200 || body?.status === "200")) {
    const error = new Error(
      body?.message || "Unable to load practice set questions."
    );
    error.response = { data: body };
    throw error;
  }

  const list = Array.isArray(body?.data)
    ? body.data
    : Array.isArray(body?.data?.questions)
      ? body.data.questions
      : Array.isArray(body?.questions)
        ? body.questions
        : [];

  return {
    questions: list,
    raw: body,
    query,
  };
};

export const fetchTopicWiseQuestions = async (params = {}) => {
  const response = await axioInstance.get(url_312, { params });
  return unwrap(response);
};

/**
 * save/mistake_book — tag or mark a mistake question as resolved
 */
export const saveMistakeBook = async (payload = {}) => {
  const bodyPayload = {
    qid: payload.qid ?? payload.question_id ?? null,
    subject: payload.subject || payload.subject_name || "",
    topic_name: payload.topic_name || payload.topicName || "",
    mistake_tag: payload.mistake_tag || payload.mistakeTag || "",
    is_resolved: Number(
      payload.is_resolved ?? (payload.resolved || payload.isResolved ? 1 : 0)
    ),
  };

  const response = await axioInstance.post(url_313, bodyPayload);
  const body = response?.data;

  if (body && !(body.status === 200 || body.status === "200")) {
    const error = new Error(
      body?.message || "Could not save mistake book changes."
    );
    error.response = { data: body };
    throw error;
  }

  return unwrap(response);
};

const isTruthyFlag = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const raw = String(value || "").trim().toLowerCase();
  return raw === "1" || raw === "true" || raw === "yes" || raw === "resolved";
};

export const normalizeMistakeTopicQuestion = (question = {}, index = 0) => {
  const status = String(
    question.status || question.question_status || question.state || ""
  ).toLowerCase();
  const isResolved =
    isTruthyFlag(question.is_resolved) ||
    isTruthyFlag(question.resolved) ||
    isTruthyFlag(question.isResolved) ||
    status.includes("resolve") ||
    status.includes("correct");

  const text =
    question.q_text ||
    question.question_text ||
    question.question ||
    question.text ||
    question.question_desc ||
    question.question_description ||
    `Question ${index + 1}`;

  const optionSource = Array.isArray(question.option)
    ? question.option
    : Array.isArray(question.options)
      ? question.options
      : [];

  const labels = ["A", "B", "C", "D", "E", "F"];
  let options = optionSource.map((opt, optIndex) => ({
    id: opt.option_id ?? opt.id ?? optIndex + 1,
    label: opt.label || labels[optIndex] || String(optIndex + 1),
    text:
      opt.option ||
      opt.option_text ||
      opt.text ||
      opt.value ||
      "",
    isCorrect:
      Number(opt.is_correct) === 1 ||
      isTruthyFlag(opt.isCorrect) ||
      isTruthyFlag(opt.correct),
    isUserAnswer:
      Number(opt.is_user_correct_ans) === 1 ||
      isTruthyFlag(opt.is_user_answer) ||
      isTruthyFlag(opt.selected),
    raw: opt,
  }));

  if (!options.length) {
    options = [1, 2, 3, 4]
      .map((n, optIndex) => {
        const textValue =
          question[`option_${n}`] ||
          question[`option${n}`] ||
          question[`opt${n}`] ||
          "";
        if (!textValue) return null;
        return {
          id: n,
          label: labels[optIndex],
          text: textValue,
          isCorrect: false,
          isUserAnswer: false,
          raw: { option_text: textValue },
        };
      })
      .filter(Boolean);
  }

  const correctAnswerRaw =
    question.answer ??
    question.correct_answer ??
    question.correct_option ??
    question.answer_id ??
    null;
  const userAnswerRaw =
    question.selected_option ??
    question.selectedOption ??
    question.user_answer ??
    question.your_answer ??
    question.attempted_answer ??
    null;

  if (correctAnswerRaw != null && options.length) {
    const correctKey = String(correctAnswerRaw).trim().toLowerCase();
    options = options.map((opt) => ({
      ...opt,
      isCorrect:
        opt.isCorrect ||
        String(opt.id).toLowerCase() === correctKey ||
        String(opt.label).toLowerCase() === correctKey,
    }));
  }

  if (userAnswerRaw != null && options.length) {
    const userKeys = Array.isArray(userAnswerRaw)
      ? userAnswerRaw.map((v) => String(v).trim().toLowerCase())
      : [String(userAnswerRaw).trim().toLowerCase()];
    options = options.map((opt) => ({
      ...opt,
      isUserAnswer:
        opt.isUserAnswer ||
        userKeys.includes(String(opt.id).toLowerCase()) ||
        userKeys.includes(String(opt.label).toLowerCase()),
    }));
  }

  return {
    id:
      question.qid ??
      question.question_id ??
      question.id ??
      `question-${index + 1}`,
    qid: question.qid ?? question.question_id ?? question.id ?? null,
    number: toNumber(
      question.display_number ??
        question.question_no ??
        question.question_number ??
        question.q_no ??
        question.serial_no ??
        index + 1,
      index + 1
    ),
    text: String(text).trim(),
    options,
    solutionText:
      question.sol_text ||
      question.solution_text ||
      question.solution ||
      question.explanation ||
      "",
    videoId:
      question.video_id_english ??
      question.solution_video_id ??
      question.video_id ??
      question.videoId ??
      null,
    mistakeTag:
      question.mistake_tag ||
      question.mistake_type ||
      question.error_tag ||
      question.tag ||
      question.reason ||
      question.mistake_category ||
      "",
    difficulty:
      question.difficulty ||
      question.toughness ||
      question.level ||
      question.difficulty_level ||
      "",
    isResolved,
    raw: question,
  };
};

/**
 * practise_solution for Mistake Book topic questions
 * GET /api/v1/practise_solution?paper_type=22&subject=Physics&topic_name=...
 */
export const fetchMistakeTopicQuestions = async (params = {}) => {
  const subject = String(params.subject || params.subject_name || "").trim();
  const topicName = String(
    params.topic_name || params.topicName || params.topic || ""
  ).trim();

  const query = {
    paper_type: Number(params.paper_type ?? params.paperType ?? 22) || 22,
  };

  if (subject) query.subject = subject;
  if (topicName) query.topic_name = topicName;

  ["paper_id", "test_id", "set_id"].forEach((key) => {
    const value = params[key];
    if (
      value !== undefined &&
      value !== null &&
      String(value).trim() !== "" &&
      String(value).trim().toLowerCase() !== "undefined"
    ) {
      query[key] = value;
    }
  });

  const response = await axioInstance.get(url_282, { params: query });
  const body = response?.data;
  const data = body?.data ?? null;

  let mistakesRaw = [];
  let resolvedRaw = [];

  if (Array.isArray(data?.mistakes) || Array.isArray(data?.resolved)) {
    mistakesRaw = Array.isArray(data.mistakes) ? data.mistakes : [];
    resolvedRaw = Array.isArray(data.resolved)
      ? data.resolved
      : Array.isArray(data.resolved_questions)
        ? data.resolved_questions
        : [];
  } else {
    const list = Array.isArray(data)
      ? data
      : Array.isArray(data?.questions)
        ? data.questions
        : Array.isArray(data?.list)
          ? data.list
          : Array.isArray(body?.questions)
            ? body.questions
            : [];

    list.forEach((item) => {
      const normalized = normalizeMistakeTopicQuestion(item);
      if (normalized.isResolved) resolvedRaw.push(item);
      else mistakesRaw.push(item);
    });
  }

  const mistakes = mistakesRaw.map((item, index) => ({
    ...normalizeMistakeTopicQuestion(item, index),
    isResolved: false,
  }));
  const resolved = resolvedRaw.map((item, index) => ({
    ...normalizeMistakeTopicQuestion(item, index),
    isResolved: true,
  }));
  const questions = [...mistakes, ...resolved];

  if (
    body &&
    !(body.status === 200 || body.status === "200") &&
    !questions.length
  ) {
    const error = new Error(
      body?.message || "Unable to load topic questions right now."
    );
    error.response = { data: body };
    throw error;
  }

  return {
    questions,
    mistakes,
    resolved,
    mistakeCount: toNumber(
      data?.mistake_count ??
        data?.mistakes_count ??
        (Array.isArray(data?.mistakes) ? mistakes.length : undefined) ??
        mistakes.length,
      mistakes.length
    ),
    resolvedCount: toNumber(
      data?.resolved_count ??
        (Array.isArray(data?.resolved) ? resolved.length : data?.resolved) ??
        resolved.length,
      resolved.length
    ),
    raw: body,
  };
};
